import { useGetOffersCargo } from "@/entities/get-offers-cargo/fetch";
import { useGetOffersCargos } from "@/entities/get-offers-cargos/fetch";
import { usePostOffersCargosReject } from "@/entities/post-offers-cargos-reject/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { fetchOfferCargoProcess } from "@/features/offer-cargo-process/fetch";
import { isSuccessStatusCode } from "@/shared/lib/http";
import { safeParseInt } from "@/shared/lib/number";
import { replaceNullWithUndefined } from "@/shared/lib/object";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import type { ShipperDeliveryProposalDetailPageProps } from "../model";

export const useShipperDeliveryProposalDetailPageProps = (
  offerCargoIdStr: string | undefined,
): ShipperDeliveryProposalDetailPageProps => {
  const deliveryProposalOfferCargoId = useMemo(() => {
    if (!offerCargoIdStr) {
      return undefined;
    }
    const id = safeParseInt(offerCargoIdStr);
    if (!id) {
      return undefined;
    }
    return id;
  }, [offerCargoIdStr]);

  if (!deliveryProposalOfferCargoId) {
    throw new Error(`配送提案IDが正しく設定されていません: ${offerCargoIdStr}`);
  }

  // Local Storage
  // 契約新規作成画面で記入途中の情報をクリアする
  const [
    _,
    setShipperContractCreatePageInProgressValues,
    removeShipperContractCreatePageInProgressValues,
  ] = useInProgressValuesLocalCache<unknown>("ShipperContractCreatePage");

  // Backend API
  const getDeliveryProposalOffersCargoQuery = useGetOffersCargo(
    {
      id: deliveryProposalOfferCargoId ?? -1,
    },
    {
      enabled: !!deliveryProposalOfferCargoId,
    },
  );
  const getOfferCargosInProcessQuery = useGetOffersCargos(
    {
      processId: getDeliveryProposalOffersCargoQuery.data?.data.process.id,
      pagination: {
        displayLimit: 10000,
      },
    },
    {
      enabled: !!getDeliveryProposalOffersCargoQuery.data,
    },
  );

  const rejectOfferCargoMutation = usePostOffersCargosReject();

  // Global Notification
  const { setGlobalNotification } = useGlobalNotification();

  // Props
  const { offerCargoProcess } = fetchOfferCargoProcess(
    getDeliveryProposalOffersCargoQuery.data?.data.process.id,
  );

  // Actions
  const navigate = useNavigate();
  const onCreateContractClick = useCallback(() => {
    if (!offerCargoIdStr) {
      return;
    }
    removeShipperContractCreatePageInProgressValues();
    navigate({
      to: "/shipper/delivery-proposals/$id/contracts/create",
      params: { id: offerCargoIdStr },
    });
  }, [
    navigate,
    removeShipperContractCreatePageInProgressValues,
    offerCargoIdStr,
  ]);
  const onRejectClick: ShipperDeliveryProposalDetailPageProps["onRejectClick"] =
    useCallback(
      (params) => {
        void (async () => {
          if (!offerCargoIdStr) {
            return;
          }
          const rejectResult = await rejectOfferCargoMutation.mutateAsync({
            id: offerCargoIdStr,
            data: params,
          });
          if (!isSuccessStatusCode(rejectResult.status)) {
            setGlobalNotification({
              variant: "error",
              message: `配送提案の見送りに失敗しました。 ${JSON.stringify(rejectResult.data)}`,
            });
            return;
          }
          // TODO: 元の荷物詳細画面に戻る (draftOfferCargoのIDを取り回す必要がある)
          setGlobalNotification({
            variant: "success",
            message: "配送提案を見送りました。",
          });
          await getDeliveryProposalOffersCargoQuery.refetch();
          await getOfferCargosInProcessQuery.refetch();
        })();
      },
      [
        setGlobalNotification,
        rejectOfferCargoMutation.mutateAsync,
        getDeliveryProposalOffersCargoQuery.refetch,
        getOfferCargosInProcessQuery.refetch,
        offerCargoIdStr,
      ],
    );
  const onRecreateContractClick = useCallback(() => {
    const contractProposalOfferCargo =
      offerCargoProcess.data?.contractOfferCargos.find(
        (offerCargo) =>
          offerCargo.sourceOfferCargoId === deliveryProposalOfferCargoId &&
          offerCargo.stage === "contract_proposal",
      );
    if (!offerCargoIdStr || !contractProposalOfferCargo) {
      return;
    }
    setShipperContractCreatePageInProgressValues(
      replaceNullWithUndefined({
        ...contractProposalOfferCargo,
        destinations: contractProposalOfferCargo.destinations.map((d) => {
          const [addressCity, addressChome] = d.address.split(" ");
          return { ...d, addressCity, addressChome };
        }),
        // workaround: vehicleWeight が string で帰ってくるので変換する
        vehicleWeight:
          safeParseInt(contractProposalOfferCargo.vehicleWeight) ?? undefined,
      }),
    );
    navigate({
      to: "/shipper/delivery-proposals/$id/contracts/create",
      params: { id: offerCargoIdStr ?? "" },
    });
  }, [
    navigate,
    setShipperContractCreatePageInProgressValues,
    offerCargoIdStr,
    deliveryProposalOfferCargoId,
    offerCargoProcess.data?.contractOfferCargos,
  ]);
  return {
    deliveryProposalOfferCargoId: deliveryProposalOfferCargoId,
    offerCargoProcess,
    onCreateContractClick,
    onRejectClick,
    onRecreateContractClick,
  };
};
